import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import HowWeWork from "@/components/HowWeWork";
import CTASection from "@/components/CTASection";
import { SERVICES, SERVICE_PILLARS } from "@/data/services";

export default function Services() {
  useEffect(() => {
    document.title = "Services | Track Digital";
  }, []);

  return (
    <main data-testid="services-page">
      <section className="pt-36 md:pt-44 pb-16 md:pb-20">
        <div className="td-container">
          <Reveal>
            <span className="td-label block mb-6">Services</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.04] max-w-4xl">
              Strategy, execution and technology in one place.
            </h1>
            <p className="td-measure text-lg md:text-xl text-[#4a4a4a] leading-relaxed mt-8">
              Some clients come to us for a single project. Others stay with us as their business
              grows. Whatever the starting point, we shape our support around your goals, your
              capacity and what will move your business forward.
            </p>
          </Reveal>
        </div>
      </section>

      {SERVICE_PILLARS.map((pillar, pi) => {
        const TINTS = [
          { bg: "#E3EEFF", frame: "#5B9BD5" },
          { bg: "#DDF2E7", frame: "#4FAE8B" },
          { bg: "#ECE9FF", frame: "#7C6CF0" },
        ];
        const t = TINTS[pi % TINTS.length];
        return (
          <section key={pillar.label} className={`py-16 md:py-20 ${pi % 2 === 1 ? "bg-[#fafafa]" : ""}`}>
            <div className="td-container">
              <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
                <div>
                  <span className="td-label block mb-4">Pillar {pi + 1}</span>
                  <h2 className="text-3xl md:text-4xl font-semibold">{pillar.label}</h2>
                </div>
                <p className="text-[#6b6b6b] text-lg max-w-sm">{pillar.copy}</p>
              </Reveal>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {pillar.items.map((slug, i) => {
                  const s = SERVICES[slug];
                  return (
                    <Reveal key={slug} delay={(i % 3) * 80}>
                      <Link
                        to={`/services/${slug}`}
                        data-testid={`services-card-${slug}`}
                        className="td-frame-card group block rounded-3xl p-8 h-full"
                        style={{ background: t.bg, "--frame": t.frame }}
                      >
                        <div className="flex items-start justify-between mb-6">
                          <h3 className="text-xl font-semibold">{s.title}</h3>
                          <ArrowUpRight size={20} className="text-black/30 group-hover:text-black/70 transition-colors shrink-0" />
                        </div>
                        <p className="text-[#4a4a55] text-[15px] leading-relaxed mb-6">{s.shortDesc || `${s.intro.split(".")[0]}.`}</p>
                        <div className="pt-5 border-t border-black/10">
                          <p className="td-label mb-2">Who it is for</p>
                          <p className="text-sm text-[#4a4a4a]">{s.whoFor[0]}</p>
                        </div>
                      </Link>
                    </Reveal>
                  );
                })}
              </div>
            </div>
          </section>
        );
      })}

      <HowWeWork />

      <CTASection
        label="Not Sure Where To Start?"
        heading="Tell us where you are today, and we'll help you decide the best way forward."
        copy="Whether you're looking for support with one project or a longer-term marketing partner, a discovery call is the simplest place to start."
      />
    </main>
  );
}
